import React, { useState, useRef } from 'react';
import { uploadFile, replaceFile, validateFile, type StorageBucket } from '../lib/api/storage';
import { captureError } from '../lib/capture-error';

interface FileUploadProps {
  bucket: StorageBucket;
  currentUrl?: string | null;
  onUploadComplete: (url: string) => void;
  label: string;
  accept?: string;
  showPreview?: boolean;
  helpText?: string;
}

const FileUpload: React.FC<FileUploadProps> = ({
  bucket,
  currentUrl,
  onUploadComplete,
  label,
  accept,
  showPreview = true,
  helpText,
}) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(currentUrl || null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setError(null);

    // Validate file
    const validationError = validateFile(file, bucket);
    if (validationError) {
      setError(validationError);
      return;
    }

    setUploading(true);

    try {
      // Upload or replace file
      const result = currentUrl
        ? await replaceFile(bucket, currentUrl, file)
        : await uploadFile(bucket, file);

      setPreviewUrl(result.url);
      onUploadComplete(result.url);

      // Clear the input so the same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      captureError(err, 'Upload error');
      setError(err instanceof Error ? err.message : 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    setPreviewUrl(null);
    onUploadComplete('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const isImage = previewUrl && (
    previewUrl.match(/\.(jpg|jpeg|png|gif|webp)$/i) ||
    previewUrl.includes('image/')
  );

  const isPdf = previewUrl && (
    previewUrl.match(/\.pdf$/i) ||
    previewUrl.includes('application/pdf')
  );

  return (
    <div className="file-upload-container">
      <label className="form-label">{label}</label>

      {/* Preview Section */}
      {showPreview && previewUrl && (
        <div className="mb-3 position-relative">
          {isImage && (
            <div className="border rounded p-2 d-inline-block">
              <img
                src={previewUrl}
                alt="Preview"
                style={{ maxWidth: '300px', maxHeight: '200px', objectFit: 'contain' }}
              />
            </div>
          )}
          {isPdf && (
            <div className="border rounded p-3 d-inline-flex align-items-center bg-light">
              <i className="fas fa-file-pdf fa-2x text-danger me-3"></i>
              <div>
                <small className="text-muted">PDF File</small>
                <br />
                <a href={previewUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-link p-0">
                  <i className="fas fa-external-link-alt me-1" aria-hidden></i>
                  View PDF
                </a>
              </div>
            </div>
          )}
          <button
            type="button"
            className="btn btn-sm btn-danger mt-2"
            onClick={handleRemove}
            disabled={uploading}
          >
            <i className="fas fa-times me-1"></i>
            Remove
          </button>
        </div>
      )}

      {/* File Input */}
      <div className="input-group">
        <input
          ref={fileInputRef}
          type="file"
          className="form-control"
          onChange={handleFileSelect}
          accept={accept}
          disabled={uploading}
        />
        {uploading && (
          <span className="input-group-text">
            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            <span className="ms-2">Uploading...</span>
          </span>
        )}
      </div>

      {/* Help Text */}
      {helpText && (
        <small className="form-text text-muted d-block mt-1">
          {helpText}
        </small>
      )}

      {/* Error Message */}
      {error && (
        <div className="alert alert-danger mt-2 mb-0" role="alert">
          <small>{error}</small>
        </div>
      )}
    </div>
  );
};

export default FileUpload;
